import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import { getOptimizedImageUrl } from '../lib/images';
import GallerySection from '../components/GallerySection';

export default function CasesPage() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Cases - Health4media.dk';
    let isMounted = true;

    async function loadCases() {
      try {
        const data = await fetchCollection('cases');
        if (isMounted) setCases(data || []);
      } catch (err) {
        console.error('Error loading cases:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadCases();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="bg-slate-50 min-h-screen">
      
      {/* HEADER HERO */}
      <section className="bg-slate-900 text-white py-10 sm:py-16 md:py-24 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Hvordan vi skaber tillid og synlighed i praksis
            </h1>
            <p className="mt-3 sm:mt-4 text-sm sm:text-lg md:text-xl text-slate-300 leading-relaxed">
              Udforsk hvordan forskellige sundhedsaktører anvender vores online vejledning, videoredigering og Meta postkalender til at styrke deres synlighed og tiltrække relevante patienter.
            </p>
          </div>
        </div>
      </section>

      {/* CASES GRID */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="divide-y divide-slate-200">
            {cases.map((c, idx) => {
              const imgUrl = c.featured_image?.[0] || '';
              const linkHref = `/cases/${c.identifier || c.slug?._ || c.id}`;
              const isEven = idx % 2 === 1;

              return (
                <div
                  key={c.id || c.identifier}
                  className={`py-12 sm:py-16 first:pt-0 last:pb-0 flex flex-col lg:flex-row gap-8 lg:gap-14 items-center ${
                    isEven ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Case Image */}
                  <div className="w-full lg:w-1/2 shrink-0">
                    <div className="relative overflow-hidden aspect-16/10 w-full">
                      {imgUrl && (
                        <img
                          src={getOptimizedImageUrl(imgUrl, { width: 900, height: 560, fit: 'cover' })}
                          alt={c.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      )}
                    </div>
                  </div>

                  {/* Case Info */}
                  <div className="w-full lg:w-1/2 space-y-5">
                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                      {c.title}
                    </h2>

                    <p className="text-base text-slate-600 leading-relaxed font-medium">
                      {c.summary}
                    </p>

                    {/* Challenge & Solution Summary Grid - Short and clean */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                      {c.challenge && (
                        <div>
                          <span className="text-xs font-bold text-slate-900 block uppercase tracking-wider mb-0.5">
                            Udfordring
                          </span>
                          <p className="text-xs text-slate-600 leading-snug line-clamp-2">
                            {c.challenge}
                          </p>
                        </div>
                      )}
                      {c.solution && (
                        <div>
                          <span className="text-xs font-bold text-slate-900 block uppercase tracking-wider mb-0.5">
                            Løsning
                          </span>
                          <p className="text-xs text-slate-600 leading-snug line-clamp-2">
                            {c.solution}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="pt-4 flex items-center justify-between">
                      <Link
                        to={linkHref}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white hover:bg-sky-700 text-sm font-semibold transition"
                      >
                        <span>Læs hele casen</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </Link>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* CTA SECTION */}
      <section className="relative overflow-hidden py-16 sm:py-24 bg-slate-900 text-white text-center">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/shutterstock-2661385173.jpg', { width: 1600, quality: 80 })}
            alt="Sundhedsvideoproduktion"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-slate-900/60" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">
            Vil du have en lignende løsning til din klinik?
          </h2>
          <p className="text-slate-200 max-w-xl mx-auto mb-8 text-sm sm:text-base leading-relaxed">
            Vi tager en uforpligtende snak om dine faglige fokusområder og hvordan vi kan hjælpe.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full bg-white hover:bg-slate-100 text-slate-950 font-bold transition shadow-lg"
          >
            <span>Book en samtale</span>
            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
          </Link>
        </div>
      </section>

      {/* GALLERI SEKTION (Nederst over footer) */}
      <GallerySection
        gallerySlug="sundhedscases-galleri"
        title="Billeder fra vores sundhedscases"
        subtitle="Glimt af hvordan sundhedsfaglig viden formidles foran kameraet."
        className="bg-white border-t border-slate-200"
      />

    </div>
  );
}
